import { useEffect } from 'react';

/**
 * Hook to handle the marquee animation
 * @param scrollRef - Reference to the scrollable element
 * @param isPaused - Whether the animation is paused
 * @param isResizing - Whether the container is being resized
 * @param direction - Direction of the marquee
 * @param speed - Speed of the marquee in pixels per second
 */
export function useMarqueeAnimation(
  scrollRef: React.RefObject<HTMLDivElement>,
  isPaused: boolean,
  isResizing: boolean,
  direction: 'left' | 'right' | 'up' | 'down',
  speed: number
) {
  useEffect(() => {
    if (!scrollRef.current || isPaused || isResizing) {
      return;
    }

    const scrollElement = scrollRef.current;
    let lastTimestamp = 0;
    let animationFrameId: number;
    let isFirstFrame = true;

    const animate = (timestamp: number) => {
      if (isFirstFrame) {
        lastTimestamp = timestamp;
        isFirstFrame = false;
        animationFrameId = requestAnimationFrame(animate);
        return;
      }

      // Limit maximum delta to prevent huge jumps after tab inactivity
      const delta = Math.min(timestamp - lastTimestamp, 100);
      lastTimestamp = timestamp;

      // Calculate pixels to move based on speed and time delta
      const pixelsToMove = (speed * delta) / 1000;

      // Use explicit direction to dictate scroll direction
      switch (direction) {
        case 'up':
          scrollElement.scrollTop += pixelsToMove;
          break;
        case 'down':
          scrollElement.scrollTop -= pixelsToMove;
          break;
        case 'left':
          scrollElement.scrollLeft += pixelsToMove;
          break;
        case 'right':
          scrollElement.scrollLeft -= pixelsToMove;
          break;
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [direction, isPaused, speed, isResizing, scrollRef]);
}
